// src/contexts/backend/AuthContext.tsx
import React, { createContext, useContext, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';

/** ===== Types ===== */
type LoginResult = { success: boolean; error?: string };
type InitiateLoginResult = { success: boolean; method?: string; error?: string; blocked?: boolean };
type PasswordResetResult = { success: boolean; error?: string };
type LoginStep = 'credentials' | '2fa' | 'authenticated';

type PendingData = {
  identityId: string;
  method: string;    // 'app' | 'email'
  timestamp: number; // ms
};

interface AuthContextType {
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  loginStep: LoginStep;
  twoFactorMethod: 'app' | 'email' | null;

  isLoginModalOpen: boolean;
  openLoginModal: () => void;
  closeLoginModal: () => void;

  rememberMePreference: boolean;
  setRememberMePreference: (rememberMe: boolean) => void;

  initiateLogin: (identifier: string, pass: string, rememberMe?: boolean) => Promise<InitiateLoginResult>;
  verify2fa: (code: string, rememberMe: boolean) => Promise<LoginResult>;
  logout: () => Promise<void>;
  resetLoginFlow: () => void;

  initiatePasswordReset: (email: string) => Promise<PasswordResetResult>;
  finalizePasswordReset: (token: string, newPass: string) => Promise<PasswordResetResult>;

  passwordResetToken: string | null;
  openPasswordResetModal: (token: string) => void;
  clearPasswordResetToken: () => void;
}

/** ===== Constants ===== */
const AuthContext = createContext<AuthContextType | undefined>(undefined);
const TWO_FA_FLOW_VALIDITY = 5 * 60 * 1000; // 5 Minuten
const PENDING_2FA_KEY = 'npt_pending_2fa';
const LOGIN_MODAL_KEY = 'npt_login_modal_open';

const saveModalOpen = (open: boolean) => {
  try {
    if (open) sessionStorage.setItem(LOGIN_MODAL_KEY, '1');
    else sessionStorage.removeItem(LOGIN_MODAL_KEY);
  } catch {}
};
const loadModalOpen = (): boolean => {
  try { return sessionStorage.getItem(LOGIN_MODAL_KEY) === '1'; } catch { return false; }
};

/** Remember-Me (nur UI-Preference, keine Auth-Daten!) */
const REMEMBER_ME_PREFERENCE_KEY = 'npt_remember_me_preference';
const saveRememberMePreference = (rememberMe: boolean) => {
  try { localStorage.setItem(REMEMBER_ME_PREFERENCE_KEY, rememberMe.toString()); } catch {}
};
const getRememberMePreference = (): boolean => {
  try { return localStorage.getItem(REMEMBER_ME_PREFERENCE_KEY) === 'true'; } catch { return false; }
};
const clearRememberMePreference = () => {
  try { localStorage.removeItem(REMEMBER_ME_PREFERENCE_KEY); } catch {}
};

const savePending2fa = (data: PendingData) => {
  try { sessionStorage.setItem(PENDING_2FA_KEY, JSON.stringify(data)); } catch {}
};
const loadPending2fa = (): PendingData | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_2FA_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as PendingData;
    if (Date.now() - data.timestamp > TWO_FA_FLOW_VALIDITY) {
      sessionStorage.removeItem(PENDING_2FA_KEY);
      return null;
    }
    return data;
  } catch { return null; }
};
const clearPending2fa = () => {
  try { sessionStorage.removeItem(PENDING_2FA_KEY); } catch {}
};

const SESSION_HINT_KEY = 'npt_had_session';
const setSessionHint = () => {
  try { localStorage.setItem(SESSION_HINT_KEY, '1'); } catch {}
};
const clearSessionHint = () => {
  try { localStorage.removeItem(SESSION_HINT_KEY); } catch {}
};
const hasSessionHint = (): boolean => {
  try { return localStorage.getItem(SESSION_HINT_KEY) === '1'; } catch { return false; }
};

const fetchAuthStatus = async (): Promise<boolean> => {
  if (!hasSessionHint()) return false;

  try {
    const res = await api.get('/auth/status', {
      validateStatus: (s) => s === 200 || s === 401,
    });
    if (res.data?.authenticated === true) return true;
    if (res.status === 401) {
      clearSessionHint();
      return false;
    }

    const refresh = await api.post('/auth/refresh', {}, {
      validateStatus: (s) => s === 200 || s === 401,
    });
    if (refresh.status !== 200) {
      clearSessionHint();
      return false;
    }

    const retry = await api.get('/auth/status', {
      validateStatus: (s) => s === 200 || s === 401,
    });
    return retry.data?.authenticated === true;
  } catch {
    return false;
  }
};

/** ===== Provider ===== */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pendingData, setPendingData] = useState<PendingData | null>(() => loadPending2fa());
  const [loginStep, setLoginStep] = useState<LoginStep>(() => loadPending2fa() ? '2fa' : 'credentials');
  const [isLoginModalOpen, setIsLoginModalOpenRaw] = useState(() => loadModalOpen() || !!loadPending2fa());
  const [rememberMePreference, setRememberMePreferenceState] = useState(() => getRememberMePreference());
  const [passwordResetToken, setPasswordResetToken] = useState<string | null>(null);

  const setIsLoginModalOpen = React.useCallback((open: boolean) => {
    setIsLoginModalOpenRaw(open);
    saveModalOpen(open);
  }, []);

  const is2faActive = loginStep === '2fa';

  const { data: isAuth, isLoading } = useQuery({
    queryKey: ['isAuthenticated'],
    queryFn: fetchAuthStatus,
    enabled: true,
    retry: false,
    staleTime: 4 * 60 * 1000,
    refetchOnWindowFocus: !is2faActive,
    refetchOnReconnect: !is2faActive,
    refetchOnMount: false,
  });

  const isAuthLoading = isLoading;
  const isAuthenticated = Boolean(isAuth);

  // Abgeleitete 2FA-Methode
  const twoFactorMethod =
    loginStep === '2fa' && pendingData ? (pendingData.method as 'app' | 'email') : null;

  const pendingDataRef = React.useRef(pendingData);
  pendingDataRef.current = pendingData;
  const loginStepRef = React.useRef(loginStep);
  loginStepRef.current = loginStep;

  /** Modal handling */
  const openLoginModal = React.useCallback(() => {
    const pd = pendingDataRef.current;
    const step = loginStepRef.current;
    if (
      pd &&
      step === '2fa' &&
      Date.now() - pd.timestamp < TWO_FA_FLOW_VALIDITY
    ) {
      // bestehende 2FA-Session fortsetzen
    } else {
      resetLoginFlow();
    }
    setIsLoginModalOpen(true);
  }, [setIsLoginModalOpen]);

  const closeLoginModal = React.useCallback(() => setIsLoginModalOpen(false), [setIsLoginModalOpen]);

  /** Flow zurücksetzen */
  const resetLoginFlow = React.useCallback(() => {
    setLoginStep('credentials');
    setPendingData(null);
    clearPending2fa();
  }, []);

  /** Remember-Me (UI) */
  const setRememberMePreference = (rememberMe: boolean) => {
    setRememberMePreferenceState(rememberMe);
    saveRememberMePreference(rememberMe);
  };

  /** Schritt 1: Credentials → ggf. 2FA */
  const initiateLogin = async (identifier: string, pass: string, rememberMe?: boolean): Promise<InitiateLoginResult> => {
    try {
      const isEmail = identifier.includes('@');
      const payload = isEmail
        ? { email: identifier, password: pass, remember_me: rememberMe }
        : { username: identifier, password: pass, remember_me: rememberMe };
      const response = await api.post('/auth/login', payload);

      if (response.data?.status === '2fa_required') {
        const pending: PendingData = {
          identityId: response.data.identity_id,
          method: response.data.method,
          timestamp: Date.now(),
        };
        setPendingData(pending);
        savePending2fa(pending);
        setLoginStep('2fa');
        return { success: true, method: response.data.method };
      }

      if (response.status === 200) {
        setSessionHint();
        await queryClient.invalidateQueries({ queryKey: ['isAuthenticated'] });
        setLoginStep('authenticated');
        navigate('/dashboard');
        return { success: true };
      }

      return { success: false, error: 'Unexpected server response.' };
    } catch (err: any) {
      const data = err?.response?.data;
      return {
        success: false,
        error: data?.error ?? err.message,
        blocked: data?.blocked === true,
      };
    }
  };

  /** Schritt 2: 2FA prüfen */
  const verify2fa = async (code: string, rememberMe: boolean): Promise<LoginResult> => {
    if (!pendingData) return { success: false, error: 'No active login attempt.' };

    try {
      const response = await api.post('/auth/2fa/verify', {
        identity_id: pendingData.identityId,
        code,
        method: pendingData.method,
        remember_me: rememberMe,
      });

      if (response.status === 200) {
        setSessionHint();
        queryClient.setQueryData(['isAuthenticated'], true);
        await queryClient.refetchQueries({ queryKey: ['isAuthenticated'] });
        setLoginStep('authenticated');
        setPendingData(null);
        clearPending2fa();
        navigate('/dashboard');
        return { success: true };
      }
      return { success: false, error: 'Verification failed.' };
    } catch (err: any) {
      return { success: false, error: err?.response?.data?.error ?? err.message };
    }
  };

  /** Logout (WS sofort schließen via Event) */
  const logout = React.useCallback(async (skipApiCall = false): Promise<void> => {
    try {
      // WebSocketContext hört auf dieses Event und schließt den Socket sofort
      window.dispatchEvent(new CustomEvent('app:logout'));
      if (!skipApiCall) {
        await api.post('/auth/logout');
      }
    } catch (err) {
    } finally {
      clearSessionHint();
      clearRememberMePreference();
      setRememberMePreferenceState(false);
      queryClient.setQueryData(['isAuthenticated'], false);
      await queryClient.invalidateQueries({ queryKey: ['isAuthenticated'] });
      resetLoginFlow();
      setIsLoginModalOpen(false);
      navigate('/');
    }
  }, [navigate, queryClient]);

  /** Handle session expiration */
  React.useEffect(() => {
    const handleSessionExpired = () => {
      logout(true).then(() => {
        // Open login modal after navigation to home page
        setTimeout(() => {
          openLoginModal();
        }, 300);
      });
    };

    window.addEventListener('app:session-expired', handleSessionExpired);
    return () => window.removeEventListener('app:session-expired', handleSessionExpired);
  }, [logout, openLoginModal]);

  const openPasswordResetModal = React.useCallback((token: string) => {
    setPasswordResetToken(token);
    resetLoginFlow();
    setIsLoginModalOpen(true);
  }, [setIsLoginModalOpen, resetLoginFlow]);

  const clearPasswordResetToken = React.useCallback(() => {
    setPasswordResetToken(null);
  }, []);

  /** PW-Reset anstoßen */
  const initiatePasswordReset = async (email: string): Promise<PasswordResetResult> => {
    try {
      await api.post('/auth/initiate-password-reset', { email });
      return { success: true };
    } catch (err: any) {
      return { success: true };
    }
  };

  /** PW-Reset finalisieren */
  const finalizePasswordReset = async (token: string, newPass: string): Promise<PasswordResetResult> => {
    try {
      await api.post('/auth/finalize-password-reset', { token, newPassword: newPass });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.response?.data?.error ?? 'An unknown error occurred.' };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAuthLoading,
        loginStep,
        twoFactorMethod,
        isLoginModalOpen,
        openLoginModal,
        closeLoginModal,
        rememberMePreference,
        setRememberMePreference,
        initiateLogin,
        verify2fa,
        logout,
        resetLoginFlow,
        initiatePasswordReset,
        finalizePasswordReset,
        passwordResetToken,
        openPasswordResetModal,
        clearPasswordResetToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/** Hook */
export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
